import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Building2, User, AtSign, Lock, Phone, Loader2, ArrowLeft,
    BadgeCheck, Users, BarChart3, ChevronDown, Gift,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, tokenStore } from "@/lib/api";
import { providerTypesApi } from "@/lib/api/providerTypes";
import LanguageToggle from "@/components/layout/LanguageToggle";

const HERO_IMAGE =
    "https://images.pexels.com/photos/263402/pexels-photo-263402.jpeg?auto=compress&cs=tinysrgb&w=1400";

const BENEFITS = [
    { icon: Users,      en: "Growing member base", ar: "قاعدة أعضاء متنامية" },
    { icon: BadgeCheck, en: "Zero commission",      ar: "بدون عمولة"          },
    { icon: BarChart3,  en: "Real-time insights",   ar: "تحليلات فورية"       },
];

export default function ProviderSignupPage() {
    const { lang, isRTL } = useI18n();
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const [form, setForm] = useState({
        corporate_name: "",
        type: "CLINIC",
        full_name: "",
        email: "",
        password: "",
        phone: "",
        referrer_code: "",
    });
    const [providerTypes, setProviderTypes] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        providerTypesApi.listPublic()
            .then((items) => setProviderTypes(items || []))
            .catch(() => setProviderTypes([]));
    }, []);

    // Pre-fill referral code from URL ?ref= or localStorage (set by member referral links)
    useEffect(() => {
        const fromUrl = (params.get("ref") || "").trim().toUpperCase();
        if (fromUrl) {
            localStorage.setItem("ref_code", fromUrl);
            setForm((f) => ({ ...f, referrer_code: fromUrl }));
            return;
        }
        const stored = (localStorage.getItem("ref_code") || "").trim().toUpperCase();
        if (stored) setForm((f) => ({ ...f, referrer_code: stored }));
    }, [params]);

    const tr = (en, ar) => lang === "ar" ? ar : en;

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const payload = {
                corporate_name: form.corporate_name.trim(),
                type: form.type,
                full_name: form.full_name.trim(),
                email: form.email.trim().toLowerCase(),
                password: form.password,
            };
            if (form.phone.trim()) payload.phone = form.phone.trim();
            if (form.referrer_code.trim()) payload.referrer_code = form.referrer_code.trim().toUpperCase();

            const { data } = await api.post("/auth/provider-signup", payload);
            if (data.access_token) tokenStore.set(data.access_token, data.refresh_token);
            await refresh();
            navigate("/provider/kyc");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409) {
                setError(tr(
                    "An account with this email already exists.",
                    "يوجد حساب مسجّل بهذا البريد الإلكتروني بالفعل."
                ));
            } else {
                setError(typeof detail === "string" ? detail : tr("Something went wrong. Please try again.", "حدث خطأ، يرجى المحاولة مرة أخرى."));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const isValid = form.corporate_name.trim() && form.full_name.trim() &&
        form.email.trim() && form.password.length >= 8;

    return (
        <div className="min-h-screen w-full flex" dir={isRTL ? "rtl" : "ltr"} data-testid="provider-signup-page">

            {/* ── Left / Form panel ─────────────────────────────────── */}
            <div className="relative flex flex-col w-full md:w-1/2 shrink-0 bg-background">

                {/* Top bar */}
                <div className="flex items-center justify-between px-8 pt-7">
                    <a
                        href="/for-providers"
                        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        {tr("Back", "رجوع")}
                    </a>
                    <LanguageToggle />
                </div>

                {/* Form area */}
                <div className="flex-1 flex flex-col justify-center px-8 sm:px-14 lg:px-20 py-10">
                    <div className="w-full max-w-md mx-auto">

                        {/* Logo + heading */}
                        <div className="mb-7">
                            <img src="/hemaia_logo.svg" alt="Hemaia" className="h-10 mb-6" />
                            <div className="inline-flex items-center gap-2 bg-primary/8 text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                                <Building2 className="h-3.5 w-3.5" />
                                {tr("Provider Registration", "تسجيل مزود خدمة")}
                            </div>
                            <h1 className="text-2xl font-semibold tracking-tight">
                                {tr("Join our provider network", "انضم إلى شبكة مزودي الخدمة")}
                            </h1>
                            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                                {tr(
                                    "Create your clinic account in seconds and start uploading your documents.",
                                    "أنشئ حساب عيادتك في ثوانٍ وابدأ رفع مستنداتك."
                                )}
                            </p>
                        </div>

                        {/* Form */}
                        <form onSubmit={onSubmit} className="space-y-3.5" noValidate>

                            {/* Organisation name */}
                            <div>
                                <label htmlFor="corporate_name" className="block text-sm font-medium mb-1.5">
                                    {tr("Organisation name", "اسم المنشأة")}
                                </label>
                                <div className="relative">
                                    <Building2 className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <input
                                        id="corporate_name"
                                        type="text"
                                        required
                                        autoFocus
                                        value={form.corporate_name}
                                        onChange={set("corporate_name")}
                                        placeholder={tr("e.g. Cairo Heart Clinic", "مثال: عيادة القلب بالقاهرة")}
                                        className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow"
                                    />
                                </div>
                            </div>

                            {/* Type */}
                            <div>
                                <label htmlFor="type" className="block text-sm font-medium mb-1.5">
                                    {tr("Type", "النوع")}
                                </label>
                                <div className="relative">
                                    <select
                                        id="type"
                                        value={form.type}
                                        onChange={set("type")}
                                        className="w-full ps-3 pe-9 py-2.5 rounded-xl border border-border bg-card text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow"
                                    >
                                        {providerTypes.map((typ) => (
                                            <option key={typ.code} value={typ.code}>
                                                {lang === "ar" ? typ.name_ar : typ.name_en}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                </div>
                            </div>

                            {/* Full name */}
                            <div>
                                <label htmlFor="full_name" className="block text-sm font-medium mb-1.5">
                                    {tr("Your full name", "الاسم الكامل")}
                                </label>
                                <div className="relative">
                                    <User className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <input
                                        id="full_name"
                                        type="text"
                                        required
                                        value={form.full_name}
                                        onChange={set("full_name")}
                                        placeholder={tr("Contact person name", "اسم الشخص المسؤول")}
                                        className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow"
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium mb-1.5">
                                    {tr("Work email", "البريد الإلكتروني للعمل")}
                                </label>
                                <div className="relative">
                                    <AtSign className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <input
                                        id="email"
                                        type="email"
                                        required
                                        autoComplete="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow"
                                    />
                                </div>
                            </div>

                            {/* Password */}
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium mb-1.5">
                                    {tr("Password", "كلمة المرور")}
                                    <span className="ms-1.5 text-xs text-muted-foreground font-normal">
                                        {tr("(min. 8 characters)", "(٨ أحرف على الأقل)")}
                                    </span>
                                </label>
                                <div className="relative">
                                    <Lock className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <input
                                        id="password"
                                        type="password"
                                        required
                                        minLength={8}
                                        autoComplete="new-password"
                                        value={form.password}
                                        onChange={set("password")}
                                        className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow"
                                    />
                                </div>
                            </div>

                            {/* Phone (optional) */}
                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium mb-1.5">
                                    {tr("Phone", "رقم الهاتف")}
                                    <span className="ms-1.5 text-xs text-muted-foreground font-normal">
                                        {tr("(optional)", "(اختياري)")}
                                    </span>
                                </label>
                                <div className="relative">
                                    <Phone className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <input
                                        id="phone"
                                        type="tel"
                                        autoComplete="tel"
                                        value={form.phone}
                                        onChange={set("phone")}
                                        className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow"
                                    />
                                </div>
                            </div>

                            {/* Referral / agent code (optional) */}
                            <div>
                                <label htmlFor="referrer_code" className="block text-sm font-medium mb-1.5">
                                    {tr("Referral code", "كود الإحالة")}
                                    <span className="ms-1.5 text-xs text-muted-foreground font-normal">
                                        {tr("(optional)", "(اختياري)")}
                                    </span>
                                </label>
                                <div className="relative">
                                    <Gift className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <input
                                        id="referrer_code"
                                        type="text"
                                        dir="ltr"
                                        value={form.referrer_code}
                                        onChange={(e) => setForm((f) => ({ ...f, referrer_code: e.target.value.toUpperCase() }))}
                                        placeholder={tr("e.g. AGENT123", "مثال: AGENT123")}
                                        className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow"
                                        data-testid="provider-signup-referrer"
                                    />
                                </div>
                                <p className="mt-1 text-xs text-muted-foreground">
                                    {tr(
                                        "If a sales agent or partner referred you, enter their code here.",
                                        "إذا أحالك مندوب أو شريك، أدخل الكود الخاص به هنا."
                                    )}
                                </p>
                            </div>

                            {error && (
                                <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting || !isValid}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-95 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-1"
                                data-testid="provider-signup-submit"
                            >
                                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                {submitting
                                    ? tr("Creating account…", "جارٍ إنشاء الحساب…")
                                    : tr("Create provider account", "إنشاء حساب مزود الخدمة")}
                            </button>
                        </form>

                        {/* Cross-links */}
                        <div className="mt-6 pt-6 border-t border-border/50 text-center space-y-2">
                            <p className="text-sm text-muted-foreground">
                                {tr("Already registered?", "لديك حساب بالفعل؟")}{" "}
                                <a href="/staff/login" className="text-primary font-semibold hover:underline underline-offset-2">
                                    {tr("Staff login →", "تسجيل الدخول →")}
                                </a>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <p className="text-center text-xs text-muted-foreground/40 pb-6 px-8">
                    © {new Date().getFullYear()} Hemaia Health Network
                </p>
            </div>

            {/* ── Right / Image panel ───────────────────────────────── */}
            <div className="hidden md:flex relative flex-1 overflow-hidden">
                <img
                    src={HERO_IMAGE}
                    alt="Clinic"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/5" />

                <div className="absolute bottom-0 start-0 end-0 p-10 text-white">
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-4">
                        {tr("Hemaia — Provider Network", "حماية — شبكة مزودي الخدمة")}
                    </p>

                    <h2 className="text-3xl lg:text-4xl font-bold leading-tight">
                        {tr(
                            "Fill more appointments.\nBuild lasting loyalty.",
                            "استقطب مرضى أكثر.\nابنِ ولاءً دائماً."
                        )}
                    </h2>

                    <p className="mt-3 text-sm text-white/65 max-w-sm leading-relaxed">
                        {tr(
                            "Join Egypt's fastest-growing healthcare network and connect with thousands of active members.",
                            "انضم إلى أسرع شبكة رعاية صحية نمواً في مصر وتواصل مع آلاف الأعضاء النشطين."
                        )}
                    </p>

                    <div className="flex flex-wrap gap-2 mt-5">
                        {BENEFITS.map(({ icon: Icon, en, ar }) => (
                            <div
                                key={en}
                                className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-3.5 py-1.5 text-white text-xs font-medium"
                            >
                                <Icon className="h-3.5 w-3.5 text-white/80 shrink-0" />
                                {lang === "ar" ? ar : en}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

        </div>
    );
}
